<script setup>
  import {computed, ref} from "vue";

  import ComponentCoreInfoDialog from "@components/Dialogs/CoreInfoDialog/CoreInfoDialog.vue";
  import ComponentRiskScoreDialog from "@components/Dialogs/RiskScoreDialog.vue";
  import ComponentIcon from "@components/Icons/Icon.vue";
  import ComponentRiskCircle from "@components/Risks/RiskCircle.vue";
  import ComponentRiskDonutChart from "@components/Risks/RiskDonutChart.vue";
  import {medicationMeal} from "@enums/medicationEnums.js";
  import {useRemember} from "@inertiajs/vue3";
  import {additionalinformationDecreed, currentSource} from "@pages/Records/Components/Result/Enums.js";
  import MedicationComponentPlanPrint from "@pages/Records/Components/Sections/MedicationBar/Components/PlanPrint.vue";
  import {MedButton, MedCheckbox, MedFormField, MedIcon, MedLabel, MedTooltip} from "@pharma4u/medicheck-components";
  import {cn} from "@pharma4u/medicheck-components/utils";

  defineOptions({
    name: "PagesRecordResultMedicationTable",
  });

  const props = defineProps({
    record: {
      type: Object,
      required: true,
    },
    medications: {
      type: Object,
      default: null,
    },
    skeleton: {
      type: Boolean,
      default: false,
    },
  });

  const refRiskScoreDialog = ref(null);
  const refCoreInfoDialog = ref(null);

  const printSettings = useRemember(
    {
      doctor: true,
      patient: true,
    },
    `PrintSettings/MedicationTable:${props.record.id}`,
  );

  const highlightSection = ref(false);

  const filteredMedications = computed(() => {
    return (props.medications ?? []).filter(
      (medication) => medication?.additionalinformation?.discontinued_by !== "doctor",
    );
  });

  function formatDate(value) {
    let date = new Date(value);
    return date.toLocaleDateString("de-DE", {dateStyle: "medium"});
  }

  function formatSelectedPackage(value) {
    return typeof value === "string"
      ? // N1, N2 and N3, which are stored within the selected_package string in
        // the database, should be hidden from this table... so we search
        // and replace them with nothing
        value.replace(" (N1)", "").replace(" (N2)", "").replace(" (N3)", "")
      : "";
  }

  function openRiskScoreDialog(medication) {
    refRiskScoreDialog.value.open(medication);
  }

  function openCoreInfoDialog(medication) {
    refCoreInfoDialog.value.open(medication);
  }

  function hasDosage(medication) {
    if (!medication || !medication.dosage) {
      return false;
    }

    if (medication.dosage.dosagealternative) {
      return true;
    }

    return (
      medication.dosage.morning > 0 ||
      medication.dosage.noon > 0 ||
      medication.dosage.evening > 0 ||
      medication.dosage.night > 0
    );
  }

  function hasDosageAlternative(medication) {
    return hasDosage(medication) && medication.dosage.dosagealternative !== null;
  }

  function hasDosageInfo(medication) {
    return medication.dosage && medication.dosage.info !== null && medication.dosage.info.length > 0;
  }

  function hasDeviationDosageInfo(deviantMedication) {
    return (
      deviantMedication &&
      deviantMedication.dosage &&
      deviantMedication.dosage.info !== null &&
      deviantMedication.dosage.info.length > 0
    );
  }

  function hasDosageMeal(medication) {
    return medication && medication.dosage && medication.dosage?.meal !== null;
  }

  function hasDosageCheckSkipped(medication) {
    return (
      (medication.statistic && medication.statistic.skipped && medication.statistic.skipped.length > 0) ||
      hasDosageAlternative(medication)
    );
  }

  function showAsteriskForSkippedDosageCheck() {
    return filteredMedications.value.some((medication) => hasDosageCheckSkipped(medication));
  }

  function showAsteriskForNonAnalysable() {
    return filteredMedications.value.some(
      (medication) => medication.abdata_key_fam === null || medication.abdata_key_fam === 0,
    );
  }
</script>

<template>
  <section
    :class="{
      'in-[.print-doc]:print:hidden': !printSettings.doctor,
      'in-[.print-patient]:print:hidden': !printSettings.patient,
      'bg-yellow-50': highlightSection,
    }"
    class="print:mt-0 print:border-2 print:border-t-0 print:p-2"
    :data-test="getTestId('result-medication')"
  >
    <h2 class="sr-only">Arzneimitteltabelle</h2>

    <div class="grid justify-end print:hidden">
      <!-- Hover effect provides visual feedback for print section, no functional impact -->
      <!-- eslint-disable-next-line vuejs-accessibility/mouse-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
      <div class="text-right text-sm" @mouseover="highlightSection = true" @mouseout="highlightSection = false">
        <div>Arzneimitteltabelle übernehmen in PDF</div>

        <div class="flex justify-end space-x-4">
          <MedFormField orientation="horizontal">
            <MedCheckbox
              v-model="printSettings.doctor"
              :data-test="getTestId('result-medication-print-doc-checkbox')"
            />
            <MedLabel>an Arzt</MedLabel>
          </MedFormField>

          <MedFormField orientation="horizontal">
            <MedCheckbox
              v-model="printSettings.patient"
              :data-test="getTestId('result-medication-print-patient-checkbox')"
            />
            <MedLabel>an Patienten</MedLabel>
          </MedFormField>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-10 gap-1 border-b border-gray-300 py-2 font-bold print:break-after-avoid-page">
      <div class="col-span-3 print:col-span-4">Arzneimittel</div>

      <div class="print:hidden">Risiko</div>

      <div class="col-span-2">Dosierung</div>

      <div>Einheit</div>

      <div class="col-span-3">Grund</div>
    </div>

    <div
      v-for="(medication, index) in filteredMedications"
      :key="index"
      class="grid grid-cols-10 gap-1 border-b border-gray-300 py-2"
      :data-test="getTestId(`result-medication-${index}`)"
    >
      <div class="col-span-3 print:col-span-4">
        <div :class="{'line-through': medication.additionalinformation?.discontinued}">
          <div>
            <span
              v-if="medication.abdata_key_fam === null || medication.abdata_key_fam === 0"
              class="inline-block pr-1"
            >
              **
            </span>

            {{ medication.fam }}

            <span v-if="medication.core.tradingstatus === 'außer Vertrieb'">a.H.</span>
          </div>

          <div class="text-xs text-gray-500">
            {{ medication.substances.map((substance) => substance.name + " " + substance.strength).join(", ") }},
            {{ medication.form }}, {{ formatSelectedPackage(medication.selected_package) }}
          </div>
        </div>

        <div v-if="medication.additionalinformation?.discontinued" class="flex flex-wrap">
          <template v-if="medication.additionalinformation.discontinued_by === 'doctor-patient-deviates'">
            <span class="mr-2 flex text-xs text-gray-500">
              <MedTooltip content="Arztangabe">
                <ComponentIcon custom="doctor" />
              </MedTooltip>
              <span>abgesetzt</span>
            </span>

            <span class="flex text-xs text-red-500">
              <MedTooltip content="Patientenangabe">
                <ComponentIcon custom="patient" />
              </MedTooltip>
              <span>weicht ab</span>
            </span>
          </template>

          <template v-else-if="medication.additionalinformation?.discontinued_by !== 'doctor'">
            <span class="flex text-xs text-red-500">
              <!-- no icon when discontinued by "unknown" (empty selection) -->
              <MedTooltip
                v-if="medication.additionalinformation.discontinued_by === 'patient'"
                content="Patientenangabe"
              >
                <ComponentIcon custom="patient" />
              </MedTooltip>
              <span>abgesetzt</span>
            </span>
          </template>
        </div>

        <div class="text-xs text-gray-500">
          <div v-if="medication.additionalinformation?.decreed">
            {{ additionalinformationDecreed[medication.additionalinformation.decreed] }}
          </div>

          <span v-if="medication.additionalinformation?.since">
            Anw. seit {{ formatDate(medication.additionalinformation.since) }}
          </span>

          <span v-if="medication.additionalinformation?.to">
            Anw. bis {{ formatDate(medication.additionalinformation.to) }}
          </span>

          <div v-if="medication.additionalinformation?.source && medication.additionalinformation?.source.length > 0">
            {{ medication.additionalinformation.source.map((source) => currentSource[source]).join(", ") }}
          </div>
        </div>
      </div>

      <div class="print:hidden">
        <div class="-mt-0.5 flex space-x-1">
          <ComponentRiskCircle :medication="medication" @open:risk-score="openRiskScoreDialog(medication)" />

          <!-- fi button -->
          <div v-if="medication.core.redhand" class="relative">
            <MedTooltip content="Rote-Hand-Brief (ggf. weitere Dokumente)">
              <MedButton
                aria-label="Fachinformationen und Rote-Hand-Brief anzeigen"
                rounded="full"
                class="size-7.5 shrink-0 bg-gray-200 p-1 text-sm hover:bg-gray-300"
                size="xs"
                :data-test="getTestId(`result-medication-${index}-open-information-dialog-button`)"
                @click="openCoreInfoDialog(medication)"
              >
                FI
              </MedButton>

              <MedIcon size="xs" icon="pan_tool" class="absolute -top-1 -right-1 text-sm text-mcred select-none" />
            </MedTooltip>
          </div>

          <div v-else>
            <MedTooltip content="Fachinformation (ggf. weitere Dokumente)">
              <MedButton
                aria-label="Fachinformationen anzeigen"
                rounded="full"
                size="xs"
                class="size-7.5 shrink-0 bg-gray-200 p-1 text-sm hover:bg-gray-300"
                :data-test="getTestId(`result-medication-${index}-open-information-dialog-button`)"
                @click="openCoreInfoDialog(medication)"
              >
                FI
              </MedButton>
            </MedTooltip>
          </div>
        </div>
      </div>

      <div class="col-span-2 flex">
        <span v-if="hasDosageCheckSkipped(medication)" class="pr-1">*</span>

        <div>
          <!-- Dosage first row -->
          <div class="w-full">
            <!-- Normal dosage -->
            <div
              v-if="
                hasDosage(medication) &&
                !hasDosage(medication.deviantmedication) &&
                !hasDosageAlternative(medication) &&
                !hasDosageAlternative(medication.deviantmedication)
              "
              class="whitespace-nowrap"
            >
              {{ medication.dosage.dosage_string_formatted }}
            </div>

            <!-- Alternative dosage from doctor -->
            <div
              v-else-if="
                hasDosage(medication) &&
                !hasDosage(medication.deviantmedication) &&
                hasDosageAlternative(medication) &&
                !hasDosageAlternative(medication.deviantmedication)
              "
            >
              wie folgt
            </div>

            <!-- Doctor dosage overridden by deviant alternative dosage -->
            <div
              v-else-if="!hasDosageAlternative(medication) && hasDosageAlternative(medication.deviantmedication)"
              class="flex text-xs text-gray-500 line-through"
            >
              <MedTooltip content="Arztangabe">
                <ComponentIcon custom="doctor" />
              </MedTooltip>

              <span class="whitespace-nowrap">{{ medication.dosage.dosage_string_formatted }}</span>
            </div>

            <!-- Doctor dosage overriden by deviant dosage -->
            <div
              v-else-if="
                hasDosage(medication) &&
                hasDosage(medication.deviantmedication) &&
                !hasDosageAlternative(medication) &&
                !hasDosageAlternative(medication.deviantmedication) &&
                !hasDosageInfo(medication)
              "
            >
              <span class="flex text-xs text-gray-500 line-through">
                <MedTooltip content="Arztangabe">
                  <ComponentIcon custom="doctor" />
                </MedTooltip>

                <span class="whitespace-nowrap">{{ medication.dosage.dosage_string_formatted }}</span>
              </span>

              <span class="flex text-xs text-red-500">
                <MedTooltip content="Patientenangabe">
                  <ComponentIcon custom="patient" />
                </MedTooltip>

                <span class="whitespace-nowrap">
                  {{ medication.deviantmedication.dosage?.dosage_string_formatted }}
                </span>
              </span>
            </div>

            <!-- no doctor dosage overridden by deviant dosage -->
            <div
              v-else-if="
                !hasDosage(medication) &&
                hasDosage(medication.deviantmedication) &&
                !hasDosageAlternative(medication) &&
                !hasDosageAlternative(medication.deviantmedication) &&
                !hasDosageInfo(medication)
              "
            >
              <MedTooltip content="Arztangabe">
                <ComponentIcon custom="doctor" />
              </MedTooltip>

              <span>k. A.</span>

              <br />

              <span class="flex text-xs text-gray-500">
                <MedTooltip content="Patientenangabe">
                  <ComponentIcon custom="patient" />
                </MedTooltip>

                <span class="whitespace-nowrap">
                  {{ medication.deviantmedication?.dosage?.dosage_string_formatted }}
                </span>
              </span>
            </div>

            <!-- doctor dosage overridden by deviant dosage -->
            <div
              v-else-if="
                hasDosage(medication) &&
                hasDosage(medication.deviantmedication) &&
                !hasDosageAlternative(medication) &&
                !hasDosageAlternative(medication.deviantmedication) &&
                hasDosageInfo(medication)
              "
            >
              <span class="flex text-xs text-gray-500 line-through">
                <MedTooltip content="Arztangabe">
                  <ComponentIcon custom="doctor" />
                </MedTooltip>

                <span class="whitespace-nowrap">{{ medication.dosage.dosage_string_formatted }}</span>
              </span>

              <span class="flex text-xs text-red-500">
                <MedTooltip content="Patientenangabe">
                  <ComponentIcon custom="patient" />
                </MedTooltip>

                <span class="whitespace-nowrap">
                  {{ medication.deviantmedication?.dosage?.dosage_string_formatted }}
                </span>
              </span>
            </div>

            <!-- no doctor dosage -->
            <div v-else-if="!hasDosage(medication)">
              <span>k. A.</span>
            </div>
          </div>

          <!-- Dosage second row -->
          <div class="w-full">
            <!-- doctor alternative dosage overridden by deviant dosage -->
            <div
              v-if="
                hasDosage(medication) && hasDosage(medication.deviantmedication) && hasDosageAlternative(medication)
              "
              class="flex text-xs text-gray-500 line-through"
            >
              <MedTooltip content="Arztangabe">
                <ComponentIcon custom="doctor" />
              </MedTooltip>

              <span>
                {{ medication.dosage.dosagealternative.text }}
              </span>
            </div>

            <!-- doctor alternative dosage -->
            <div
              v-else-if="
                hasDosage(medication) && !hasDosage(medication.deviantmedication) && hasDosageAlternative(medication)
              "
              class="flex text-xs text-gray-500"
            >
              {{ medication.dosage.dosagealternative.text }}
            </div>

            <!-- -- -- -- -- -- -- -->
            <!--DosageMessageString-->
            <!-- dosageInfo overridden by deviant dosageInfo -->
            <div
              v-if="hasDosageInfo(medication) && hasDeviationDosageInfo(medication.deviantmedication)"
              class="flex text-xs text-gray-500 line-through"
            >
              <MedTooltip content="Arztangabe">
                <ComponentIcon custom="doctor" />
              </MedTooltip>

              <span>{{ medication.dosage.info }}</span>
            </div>

            <!-- dosage info -->
            <span v-else-if="hasDosageInfo(medication)" class="flex text-xs text-gray-500">
              <span>{{ medication.dosage.info }}</span>
            </span>

            <!-- -- -- -- -- -- -- -->
            <!--PatientDosageAlternativeString-->
            <!-- alternative doctor dosage overridden by alternative deviant dosage -->
            <!-- OR doctor dosage and doctor alternative overridden by deviant dosage and deviant alternative -->
            <div
              v-if="
                (!hasDosageAlternative(medication) && hasDosageAlternative(medication.deviantmedication)) ||
                (hasDosage(medication) &&
                  hasDosage(medication.deviantmedication) &&
                  hasDosageAlternative(medication) &&
                  hasDosageAlternative(medication.deviantmedication))
              "
              class="flex text-xs text-red-500"
            >
              <MedTooltip content="Patientenangabe">
                <ComponentIcon custom="patient" />
              </MedTooltip>

              <span>
                {{ medication.deviantmedication?.dosage?.dosagealternative.text }}
              </span>
            </div>

            <!-- doctor dosage and doctor alternative overridden by deviant dosage -->
            <div
              v-else-if="
                hasDosage(medication) &&
                hasDosage(medication.deviantmedication) &&
                hasDosageAlternative(medication) &&
                !hasDosageAlternative(medication.deviantmedication)
              "
              class="flex text-xs text-red-500"
            >
              <MedTooltip content="Patientenangabe">
                <ComponentIcon custom="patient" />
              </MedTooltip>

              <span class="whitespace-nowrap">
                {{ medication.deviantmedication.dosage?.dosage_string_formatted }}
              </span>
            </div>

            <!-- -- -- -- -- -- -- -->
            <!--PatientDosageMessageString-->
            <span v-if="hasDeviationDosageInfo(medication.deviantmedication)" class="flex text-xs text-gray-500">
              <MedTooltip content="Patientenangabe">
                <ComponentIcon custom="patient" />
              </MedTooltip>

              <span>{{ medication.deviantmedication?.dosage?.info }}</span>
            </span>

            <!-- -- -- -- -- -- -- -->
            <!-- meal context -->
            <div v-if="hasDosageMeal(medication) || hasDosageMeal(medication.deviantmedication)">
              <span
                v-if="hasDosageMeal(medication)"
                :class="
                  cn('flex text-xs text-gray-500', {
                    'line-through':
                      hasDosageMeal(medication.deviantmedication) &&
                      medication?.dosage?.meal !== medication?.deviantmedication?.dosage?.meal,
                  })
                "
              >
                <MedTooltip
                  v-if="
                    hasDosageMeal(medication) &&
                    hasDosageMeal(medication.deviantmedication) &&
                    medication?.dosage?.meal !== medication?.deviantmedication?.dosage?.meal
                  "
                  content="Arztangabe"
                >
                  <ComponentIcon custom="doctor" />
                </MedTooltip>
                <span>{{ medicationMeal[medication?.dosage?.meal] }}</span>
              </span>

              <span
                v-if="
                  hasDosageMeal(medication.deviantmedication) &&
                  medication?.dosage?.meal !== medication?.deviantmedication?.dosage?.meal
                "
                :class="
                  cn('flex text-xs', {
                    'text-red-500':
                      hasDosageMeal(medication) &&
                      medication?.dosage?.meal !== medication?.deviantmedication?.dosage?.meal,
                    'text-gray-500': !hasDosageMeal(medication),
                  })
                "
              >
                <MedTooltip
                  v-if="
                    hasDosageMeal(medication) &&
                    hasDosageMeal(medication.deviantmedication) &&
                    medication?.dosage?.meal !== medication?.deviantmedication?.dosage?.meal
                  "
                  content="Patientenangabe"
                >
                  <ComponentIcon custom="patient" />
                </MedTooltip>
                <span>{{ medicationMeal[medication?.deviantmedication?.dosage?.meal] }}</span>
              </span>
            </div>
          </div>
        </div>
      </div>

      <div class="break-all">
        {{ medication.deviantmedication?.dosage?.selected_unit || medication.dosage.selected_unit }}
      </div>

      <div class="col-span-3">
        <div>
          <!--IndicationString-->
          <span>
            <span
              v-if="medication.indications.length > 0 && medication.deviantmedication?.indications.length > 0"
              class="flex text-xs text-gray-500 line-through"
            >
              <MedTooltip content="Arztangabe">
                <ComponentIcon custom="doctor" />
              </MedTooltip>

              <span>
                {{ medication.indications.map((indication) => indication.name || indication.name_icdc).join(", ") }}
              </span>
            </span>

            <span
              v-else-if="medication.deviantmedication?.indications.length > 0 && medication.indications.length === 0"
              class="flex"
            >
              <MedTooltip content="Arztangabe">
                <ComponentIcon custom="doctor" />
              </MedTooltip>

              <span>k. A.</span>
            </span>

            <span v-else>
              {{ medication.indications.map((indication) => indication.name || indication.name_icdc).join(", ") }}
            </span>
          </span>
        </div>

        <div>
          <!--PatientIndicationString-->
          <span v-if="medication.deviantmedication?.indications.length > 0" class="flex text-xs text-red-500">
            <MedTooltip content="Patientenangabe">
              <ComponentIcon custom="patient" />
            </MedTooltip>

            <span>
              {{
                medication.deviantmedication?.indications
                  .map((indication) => indication.name || indication.name_icdc)
                  .join(", ")
              }}
            </span>
          </span>
        </div>
      </div>
    </div>

    <div class="grid grid-cols-10 gap-1 py-2 print:break-before-avoid-page">
      <div class="col-span-3 self-center">
        <div class="print:hidden">Arznei&shy;mittel&shy;risiken (gesamt):</div>
      </div>

      <div class="col-span-4 self-center">
        <div class="-ml-2 print:hidden">
          <ComponentRiskDonutChart :medications="filteredMedications" />
        </div>
      </div>

      <dl class="col-span-3 self-center">
        <div v-if="showAsteriskForSkippedDosageCheck" class="flex">
          <dt class="w-5">*</dt>
          <dd>keine Dosis&shy;prüfung möglich</dd>
        </div>

        <div v-if="showAsteriskForNonAnalysable" class="flex">
          <dt class="w-5">**</dt>
          <dd>nicht analysierbar</dd>
        </div>
      </dl>
    </div>

    <div v-if="$page.props.client.license.is_pro" class="mt-4 flex flex-row-reverse print:hidden">
      <MedicationComponentPlanPrint />
    </div>
  </section>

  <ComponentRiskScoreDialog ref="refRiskScoreDialog" />
  <ComponentCoreInfoDialog ref="refCoreInfoDialog" />
</template>
